import { useFieldArray } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faMinusCircle, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCustomField } from 'components/custom-fields/CustomFieldForm';
import { toFriendlyType } from 'components/custom-fields/helpers';
import * as Form from 'components/form';
import { twMerge } from 'tailwind-merge';

const CustomFieldOptionForm = () => {
    const methods = Form.useGivebutterFormContext();
    const { control, getValues } = methods;
    const { customField, getFormFieldPath } = useCustomField();
    const isEditing = Boolean(customField);

    const type = getValues(getFormFieldPath('type'));
    const isOption = toFriendlyType(type) === 'option';

    const OPTIONS_LIMIT = 100;

    const { fields, append, remove } = useFieldArray({
        name: getFormFieldPath('answers'),
        rules: {
            required: isOption && 'Must provide at least 1 option.',
            validate: !isOption
                ? undefined
                : value =>
                      Boolean(
                          value.filter(o => (o as { value: string }).value.trim().length).length,
                      ) || 'Must provide at least 1 option',
        },
        control,
    });

    const isExisting = (field: (typeof fields)[0]) => {
        /* @ts-ignore */
        const isNotDuplicate = fields.filter(f => f.value === field.value).length <= 1;
        /* @ts-ignore */
        const isExistingAnswer = (customField?.answers ?? []).includes(field.value);

        return isExistingAnswer && isNotDuplicate;
    };

    if (!isOption) {
        return null;
    }

    return (
        <>
            <div className={'flex w-full flex-col gap-y-1.5'}>
                <ul>
                    {fields.map((field, index) => {
                        const isReadOnly = isEditing && isExisting(field);

                        return (
                            <li
                                key={field.id}
                                className={
                                    'flex items-center justify-between border border-b-0 border-gray-200 bg-white first:rounded-t last:rounded-b last:border-b'
                                }
                            >
                                <Form.TextInputRoot
                                    control={control}
                                    name={getFormFieldPath(`answers.${index}.value`)}
                                    inputRootProps={{
                                        className: 'w-full ring-0',
                                    }}
                                >
                                    <Form.TextInputForm
                                        placeholder={'Enter option'}
                                        readOnly={isReadOnly}
                                        className={
                                            isReadOnly ? 'cursor-not-allowed opacity-30' : ''
                                        }
                                    />
                                </Form.TextInputRoot>
                                {index !== 0 && (!isEditing || !isExisting(field)) && (
                                    <Button
                                        variant="none"
                                        color="error"
                                        onClick={() => remove(index)}
                                    >
                                        <FontAwesomeIcon icon={faMinusCircle} className={'mr-4'} />
                                    </Button>
                                )}
                            </li>
                        );
                    })}

                    {fields.length < OPTIONS_LIMIT && (
                        <li
                            className={
                                'border border-gray-200 bg-gray-50 first:rounded-t last:rounded-b'
                            }
                        >
                            <Button
                                variant="transparent"
                                size="sm"
                                className="font-medium"
                                onClick={() =>
                                    fields.length < OPTIONS_LIMIT ? append({ value: '' }) : null
                                }
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span>Add Option</span>
                            </Button>
                        </li>
                    )}
                </ul>

                <div className={'flex justify-between'}>
                    <div>
                        <Form.Error name={getFormFieldPath('answers')} />
                        <Form.Error name={getFormFieldPath('answers.0')} />
                    </div>
                    <p className={'text-xs text-gray-600'}>
                        {fields.length} / {OPTIONS_LIMIT}
                    </p>
                </div>
            </div>

            <Form.Container
                className={twMerge('flex flex-col gap-y-1.5', isEditing && 'opacity-50')}
            >
                <Form.Label name={getFormFieldPath('type')}>Field Style</Form.Label>
                <Form.SingleSelect
                    control={control}
                    name={getFormFieldPath('type')}
                    disabled={isEditing}
                >
                    <SingleSelect.Trigger aria-label="Field style" disabled={isEditing} />
                    <SingleSelect.Content>
                        <SingleSelect.Item value="select">
                            <SingleSelect.ItemText>Dropdown (single-select)</SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="multiselect">
                            <SingleSelect.ItemText>Checkbox (multi-select)</SingleSelect.ItemText>
                        </SingleSelect.Item>
                    </SingleSelect.Content>
                </Form.SingleSelect>
            </Form.Container>
        </>
    );
};

const Option = CustomFieldOptionForm;

export { CustomFieldOptionForm, Option };
